import { memo, useState, useEffect, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  SubTitle,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { useChartData } from "./hooks/useChartData";
import ChartTypeToggle from "./components/ChartTypeToggle";
import DateRangeFilter from "./components/DateRangeFilter";
import { getFirstTicketDate } from "./helpers/processTickets";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  SubTitle,
  Tooltip,
  Legend,
  Filler
);

function TicketChart({
  tickets, compareTickets, eventName, compareEventName,
  eventDays, isCompareLoading,
  eventStartDate, eventEndDate,
  compareEventStartDate, compareEventEndDate,
}) {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState("line");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Reset date range when event changes
  useEffect(() => {
    setDateFrom(null);
    setDateTo(null);
  }, [eventStartDate, compareEventStartDate]);

  // Compute min/max selectable dates from ticket data
  const { minDate, maxDate } = useMemo(() => {
    const firstPrimary = getFirstTicketDate(tickets);
    const firstCompare = compareTickets?.length ? getFirstTicketDate(compareTickets) : null;
    const min = firstPrimary && firstCompare
      ? (firstPrimary < firstCompare ? firstPrimary : firstCompare)
      : firstPrimary || firstCompare;

    const today = new Date().toISOString().substring(0, 10);
    const ends = [today];
    if (eventEndDate) ends.push(eventEndDate.substring(0, 10));
    if (compareEventEndDate) ends.push(compareEventEndDate.substring(0, 10));
    const max = ends.sort().pop();

    return {
      minDate: min ? new Date(min) : undefined,
      maxDate: max ? new Date(max) : undefined,
    };
  }, [tickets, compareTickets, eventEndDate, compareEventEndDate]);

  const { chartData, options, isComparing } = useChartData({
    tickets, compareTickets, eventName, compareEventName, eventDays,
    dateFrom, dateTo, eventStartDate, compareEventStartDate,
  });

  if (!chartData) return (
    <div className="mb-4 text-center py-5 bg-light rounded">
      <i className="fas fa-chart-line fa-3x text-muted mb-3 d-block" />
      <p className="text-muted mb-0">{t("No ticket data available for this event.")}</p>
    </div>
  );

  const ChartComponent = chartType === "bar" ? Bar : Line;

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-end mb-2 flex-wrap gap-2">
        <DateRangeFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          minDate={minDate}
          maxDate={maxDate}
        />
        <ChartTypeToggle chartType={chartType} setChartType={setChartType} />
      </div>
      <div className="position-relative" style={{ height: "400px" }}>
        <ChartComponent data={chartData} options={options} />
        {isCompareLoading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2 mb-0">{t("Loading comparison data...")}</p>
            </div>
          </div>
        )}
      </div>
      <p className="text-muted small mt-2 mb-0">
        <strong>{t("Daily tickets")}</strong> ({t("left axis")}): {t("Number of tickets booked on a specific day.")}
        <br />
        <strong>{t("Cumulative")}</strong> ({t("right axis")}): {t("The running total of all booked tickets. Example: If 5 tickets were booked on the first day and 3 on the second day, the cumulative line shows the value 8 on the second day.")}
        {eventDays?.length > 1 && !isComparing && (
          <>
            <br />
            <strong>{t("Tip")}</strong>: {t("Click on the event days in the legend above to show or hide their individual booking progress.")}
          </>
        )}
      </p>
    </div>
  );
}

export default memo(TicketChart);
